import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Pause, Play, Upload } from 'lucide-react';
import type { TimelineIR, TimelineScene } from '@rehub/propertycut-timeline';
import {
  activeVisuals,
  averagePatch,
  fontStack,
  gradeFilterTables,
  logoOverlaySpans,
  resolveTextStyle,
  speedAtSceneTime,
  TEXT_SHADOW,
  videoSourcePoint,
  DEFAULT_PLATE_LOGO_SCALE,
  MAX_OVERLAY_LOGO_SCALE,
  MAX_PLATE_LOGO_SCALE,
  MIN_CLIP_SCALE,
  MIN_OVERLAY_LOGO_SCALE,
  MIN_PLATE_LOGO_SCALE,
} from '@rehub/propertycut-timeline';
import type { RenderFormat } from '../../types';
import { cropStyle, formatClock, motionCss } from './model';

interface PreviewStageProps {
  ir: TimelineIR;
  format: RenderFormat;
  empty: boolean;
  time: number;
  duration: number;
  playing: boolean;
  issues: string[];
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onBrowse: () => void;
  onDropFiles: (files: File[]) => void;
  onMediaError: (name: string) => void;
  nameForUrl: (url: string) => string;
  selectedPlateId: string | null;
  overlaySelected: boolean;
  onSelectPlate: (clipId: string) => void;
  onSelectOverlay: () => void;
  onPlateLogoScale: (clipId: string, scale: number, commit: boolean) => void;
  onOverlayLogoScale: (scale: number, commit: boolean) => void;
  onClipScale: (clipId: string, scale: number, commit: boolean) => void;
  onClipMove: (clipId: string, offsetX: number, offsetY: number, commit: boolean) => void;
  /** When set, a click on this clip samples a neutral colour for white balance. */
  neutralPickClipId?: string | null;
  onPickedNeutral?: (clipId: string, r: number, g: number, b: number) => void;
  onPickBlocked?: () => void;
}

export type SampledFrame = ImageData | 'blocked' | 'missing';

export interface PreviewStageHandle {
  sampleClip(clipId: string): Promise<SampledFrame>;
}

function readVideoFrame(el: HTMLVideoElement): SampledFrame {
  const w = el.videoWidth;
  const h = el.videoHeight;
  if (!w || !h) return 'missing';
  const scale = Math.min(1, 360 / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 'missing';
  try {
    ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    return 'blocked';
  }
}

const PreviewStage = forwardRef<PreviewStageHandle, PreviewStageProps>(function PreviewStage({
  ir,
  format,
  empty,
  time,
  duration,
  playing,
  issues,
  onTogglePlay,
  onSeek,
  onBrowse,
  onDropFiles,
  onMediaError,
  nameForUrl,
  selectedPlateId,
  overlaySelected,
  onSelectPlate,
  onSelectOverlay,
  onPlateLogoScale,
  onOverlayLogoScale,
  onClipScale,
  onClipMove,
  neutralPickClipId,
  onPickedNeutral,
  onPickBlocked,
}, ref) {
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const gradeFilters = useMemo(
    () =>
      ir.scenes.flatMap((scene) => {
        if (scene.kind !== 'video' || !scene.grade) return [];
        return [{ id: scene.id, ...gradeFilterTables(scene.grade) }];
      }),
    [ir.scenes],
  );

  useImperativeHandle(ref, () => ({
    async sampleClip(clipId: string) {
      const deadline = Date.now() + 2000;
      while (Date.now() < deadline) {
        const el = videoRefs.current[clipId];
        if (el && el.readyState >= 2 && !el.seeking && el.videoWidth > 0) return readVideoFrame(el);
        await new Promise((resolve) => setTimeout(resolve, 40));
      }
      return 'missing';
    },
  }));

  const pickNeutral = (event: React.MouseEvent<HTMLVideoElement>, scene: TimelineScene) => {
    if (neutralPickClipId !== scene.id) return;
    event.stopPropagation();
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const boxed = (scene.scale ?? 1) < 1;
    const point = videoSourcePoint({
      clickX: event.clientX - rect.left,
      clickY: event.clientY - rect.top,
      elementWidth: rect.width,
      elementHeight: rect.height,
      videoWidth: el.videoWidth,
      videoHeight: el.videoHeight,
      fit: boxed || scene.fit === 'contain' ? 'contain' : 'cover',
      positionX: boxed ? 0.5 : ((scene.offset?.x ?? 0) + 1) / 2,
      positionY: boxed ? 0.5 : (1 - (scene.offset?.y ?? 0)) / 2,
    });
    if (!point) return;
    const frame = readVideoFrame(el);
    if (frame === 'blocked') {
      onPickBlocked?.();
      return;
    }
    if (frame === 'missing') return;
    const rgb = averagePatch(frame.data, frame.width, frame.height, point.x, point.y);
    onPickedNeutral?.(scene.id, rgb.r, rgb.g, rgb.b);
  };
  const hits = activeVisuals(ir.scenes, time);
  const logoSpan = ir.logoOverlay
    ? logoOverlaySpans(ir.logoOverlay).find((span) => time >= span.start && time < span.start + span.length)
    : undefined;
  const plateLogoOnScreen = hits.some((hit) => {
    const scene = ir.scenes[hit.index];
    return scene?.kind === 'logoBumper' && Boolean(scene.bumper?.logoUrl);
  });
  const aspect = format === 'vertical' ? '9 / 16' : format === 'square' ? '1 / 1' : '16 / 9';
  const widthOverHeight = format === 'vertical' ? 9 / 16 : format === 'square' ? 1 : 16 / 9;

  useEffect(() => {
    for (const hit of hits) {
      const scene = ir.scenes[hit.index];
      if (scene.kind !== 'video' || !scene.src) continue;
      const el = videoRefs.current[scene.id];
      if (!el) continue;
      const ramp = scene.speed?.length ? speedAtSceneTime(scene.speed, hit.local) : null;
      const target = ramp ? ramp.source : (scene.trim ?? 0) + hit.local;
      const rate = ramp?.speed ?? 1;
      const drift = playing ? 0.35 * Math.max(1, rate) : 0.05;
      // A fast clip can outrun the decoder; re-seeking before the last seek lands only stalls it further.
      const busy = playing && el.seeking;
      if (!busy && (!Number.isFinite(el.currentTime) || Math.abs(el.currentTime - target) > drift)) {
        try { el.currentTime = target; } catch { /* metadata not ready */ }
      }
      if (Math.abs(el.playbackRate - rate) > 0.001) el.playbackRate = rate;
      const volume = rate === 1 ? Math.max(0, Math.min(1, scene.volume ?? 0)) : 0;
      el.volume = volume;
      el.muted = volume <= 0.001;
      if (playing) {
        if (el.paused) void el.play().catch(() => undefined);
      } else {
        el.pause();
      }
    }
  }, [playing, time, ir.scenes]);

  useEffect(() => {
    const clips = ir.audioClips ?? [];
    clips.forEach((clip, index) => {
      const el = audioRefs.current[String(index)];
      if (!el) return;
      const inside = time >= clip.start && time < clip.start + clip.length;
      if (!inside) {
        el.pause();
        return;
      }
      const local = time - clip.start;
      const target = clip.trim + local;
      if (Math.abs(el.currentTime - target) > (playing ? 0.35 : 0.05)) {
        el.currentTime = target;
      }
      const remaining = clip.length - local;
      let volume = clip.volume;
      if (clip.fadeIn > 0 && local < clip.fadeIn) volume *= local / clip.fadeIn;
      if (clip.fadeOut > 0 && remaining < clip.fadeOut) volume *= Math.max(0, remaining / clip.fadeOut);
      el.volume = Math.max(0, Math.min(1, volume));
      if (playing) void el.play().catch(() => undefined);
      else el.pause();
    });
  }, [ir.audioClips, playing, time]);

  return (
    <div
      className="flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-950"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.files?.length) return;
        e.preventDefault();
        onDropFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div
        className="flex-1 min-h-0 overflow-hidden p-2 md:p-4 grid place-items-center"
        style={{ containerType: 'size' }}
      >
          <div
            data-preview-frame
            className="relative bg-black rounded-lg overflow-hidden shadow-2xl shadow-black/40 border border-white/10"
            style={{
              aspectRatio: aspect,
              containerType: 'size',
              width: `min(100cqw, calc(100cqh * ${widthOverHeight}))`,
              height: `min(100cqh, calc(100cqw / ${widthOverHeight}))`,
            }}
          >
            {gradeFilters.length > 0 && (
              <svg width="0" height="0" className="absolute" aria-hidden>
                <defs>
                  {gradeFilters.map((filter) => (
                    <filter id={`pc-grade-${filter.id}`} key={filter.id} colorInterpolationFilters="sRGB">
                      <feComponentTransfer>
                        <feFuncR type="table" tableValues={filter.r} />
                        <feFuncG type="table" tableValues={filter.g} />
                        <feFuncB type="table" tableValues={filter.b} />
                      </feComponentTransfer>
                    </filter>
                  ))}
                </defs>
              </svg>
            )}
            {empty ? (
              <button
                type="button"
                onClick={onBrowse}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center hover:bg-white/[0.03]"
              >
                <span className="w-12 h-12 rounded-full bg-teal-500/15 text-teal-200 grid place-items-center">
                  <Upload className="w-5 h-5" />
                </span>
                <span className="text-sm text-slate-200">Add photos or video clips to get started.</span>
                <span className="text-xs text-slate-500">Drop files here, or click to upload</span>
              </button>
            ) : (
              ir.scenes.map((scene, index) => {
                const hit = hits.find((item) => item.index === index);
                if (!hit) return null;
                if (scene.kind === 'logoBumper') {
                  return (
                    <div
                      key={scene.id}
                      className="absolute inset-0"
                      style={{ background: scene.brandColor || '#0f172a', ...joinStyle(hit) }}
                    >
                      {scene.bumper?.logoUrl && (
                        <ResizableLogo
                          src={scene.bumper.logoUrl}
                          scale={scene.bumper.scale ?? DEFAULT_PLATE_LOGO_SCALE}
                          min={MIN_PLATE_LOGO_SCALE}
                          max={MAX_PLATE_LOGO_SCALE}
                          selected={selectedPlateId === scene.id}
                          showHandle={!playing}
                          motionStyle={playing ? motionCss(scene.bumper.motion, hit.local, scene.length) : undefined}
                          label="Logo on this plate"
                          placement={{ x: scene.bumper.x ?? 0.5, y: scene.bumper.y ?? 0.5 }}
                          onSelect={() => onSelectPlate(scene.id)}
                          onScale={(scale, commit) => onPlateLogoScale(scene.id, scale, commit)}
                        />
                      )}
                    </div>
                  );
                }
                if (!scene.src) return null;
                const scale = scene.scale ?? 1;
                const box = scale < 1 ? scaledBox(scale, scene.offset?.x ?? 0, scene.offset?.y ?? 0) : null;
                const style: React.CSSProperties = box
                  ? { ...box, ...joinStyle(hit), objectFit: 'contain' }
                  : {
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      ...joinStyle(hit),
                      objectFit: scene.fit === 'contain' ? 'contain' : 'cover',
                      objectPosition: cropStyle(scene.offset?.x ?? 0, scene.offset?.y ?? 0),
                    };
                if (scene.grade) style.filter = `url(#pc-grade-${scene.id})`;
                const media = scene.kind === 'video' ? (
                  <video
                    key={scene.id}
                    ref={(node) => {
                      videoRefs.current[scene.id] = node;
                    }}
                    src={scene.src}
                    className={`absolute ${neutralPickClipId === scene.id ? 'cursor-crosshair' : ''}`}
                    style={style}
                    muted={(scene.volume ?? 0) <= 0.001}
                    onClick={(event) => pickNeutral(event, scene)}
                    playsInline
                    preload="auto"
                    onError={() => onMediaError(nameForUrl(scene.src!))}
                  />
                ) : (
                  <img
                    key={scene.id}
                    src={scene.src}
                    alt=""
                    className="absolute"
                    style={style}
                    onError={() => onMediaError(nameForUrl(scene.src!))}
                  />
                );
                return (
                  <React.Fragment key={scene.id}>
                    {media}
                    {!playing && !neutralPickClipId && selectedPlateId === scene.id && <ResizableClip
                      scale={scale}
                      offsetX={scene.offset?.x ?? 0}
                      offsetY={scene.offset?.y ?? 0}
                      onScale={(next, commit) => onClipScale(scene.id, next, commit)}
                      onMove={(x, y, commit) => onClipMove(scene.id, x, y, commit)}
                    />}
                  </React.Fragment>
                );
              })
            )}

            {(ir.texts ?? []).map((text, index) => {
              const visible = time >= text.start && time < text.start + text.length;
              if (!visible) return null;
              const look = text.style ?? resolveTextStyle({});
              const motion = playing ? motionCss(text.motion, time - text.start, text.length) : {};
              return (
                <div
                  key={`${text.text}-${index}`}
                  className="absolute z-[25] pointer-events-none"
                  style={{
                    left: `${text.x * 100}%`,
                    top: `${text.y * 100}%`,
                    width: `${look.width * 100}%`,
                    transform: 'translate(-50%, -50%)',
                    textAlign: text.align,
                  }}
                >
                  <span
                    className="inline-block whitespace-pre-wrap"
                    style={{
                      ...motion,
                      opacity: look.opacity * (typeof motion.opacity === 'number' ? motion.opacity : 1),
                      color: text.color,
                      fontSize: `max(8px, calc(${text.fontSize / 1080} * 100cqh))`,
                      fontFamily: fontStack(look.fontFamily),
                      fontWeight: look.fontWeight,
                      fontStyle: look.italic ? 'italic' : 'normal',
                      letterSpacing: `${look.letterSpacing}em`,
                      lineHeight: look.lineHeight,
                      textTransform: look.uppercase ? 'uppercase' : 'none',
                      textShadow: look.shadow ? TEXT_SHADOW : 'none',
                      background: text.background ?? 'transparent',
                      padding: text.background ? '0.25em 0.5em' : 0,
                      borderRadius: text.background ? '0.25em' : 0,
                    }}
                  >
                    {text.text}
                  </span>
                </div>
              );
            })}

            {ir.logoOverlay && logoSpan && (
              <ResizableLogo
                src={ir.logoOverlay.src}
                scale={ir.logoOverlay.scale}
                min={MIN_OVERLAY_LOGO_SCALE}
                max={MAX_OVERLAY_LOGO_SCALE}
                selected={overlaySelected}
                showHandle={!playing}
                motionStyle={playing ? motionCss(ir.logoOverlay.motion, time - logoSpan.start, logoSpan.length) : undefined}
                label="Logo on the video"
                placement={{ x: ir.logoOverlay.x, y: ir.logoOverlay.y }}
                onSelect={onSelectOverlay}
                onScale={onOverlayLogoScale}
              />
            )}
            {neutralPickClipId && (
              <div className="absolute top-2 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/75 px-3 py-1 text-[11px] text-white pointer-events-none">
                Click a white or grey surface
              </div>
            )}
            {!playing && !neutralPickClipId && (selectedPlateId || overlaySelected || plateLogoOnScreen) && (
              <div className="absolute top-2 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/70 px-2.5 py-1 text-[11px] text-white pointer-events-none">
                Drag the corner to resize
              </div>
            )}
          </div>
      </div>

      {(ir.audioClips ?? []).map((clip, index) => (
        <audio
          key={`${clip.src}-${index}`}
          ref={(node) => {
            audioRefs.current[String(index)] = node;
          }}
          src={clip.src}
          preload="auto"
        />
      ))}

      {issues.length > 0 && (
        <div className="mx-3 sm:mx-6 mb-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
          {issues[0]}
        </div>
      )}

      <div className="shrink-0 px-3 sm:px-6 pt-1 pb-3 sm:pb-4 flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={onTogglePlay}
          disabled={duration <= 0}
          className="w-9 h-9 rounded-full bg-white text-slate-950 grid place-items-center disabled:opacity-40"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={0.05}
          value={Math.min(time, duration || 0)}
          onChange={(e) => onSeek(Number(e.target.value))}
          className="flex-1 accent-teal-400"
          aria-label="Preview position"
        />
        <span className="text-[11px] sm:text-xs tabular-nums text-slate-300 shrink-0 whitespace-nowrap">
          {formatClock(time)} / {formatClock(duration)}
        </span>
      </div>
    </div>
  );
});

export default PreviewStage;

/** Frame-shaped box for a clip scaled below full frame, matching Shotstack's scale + offset. */
function joinStyle(hit: { opacity: number; clipPath?: string; transform?: string; maskImage?: string }): React.CSSProperties {
  const style: React.CSSProperties = { opacity: hit.opacity };
  if (hit.clipPath) style.clipPath = hit.clipPath;
  if (hit.transform) {
    style.transform = hit.transform;
    style.transformOrigin = 'center center';
  }
  if (hit.maskImage) {
    style.maskImage = hit.maskImage;
    style.WebkitMaskImage = hit.maskImage;
  }
  return style;
}

function scaledBox(scale: number, offsetX: number, offsetY: number): React.CSSProperties {
  return {
    left: `${(0.5 + offsetX - scale / 2) * 100}%`,
    top: `${(0.5 - offsetY - scale / 2) * 100}%`,
    width: `${scale * 100}%`,
    height: `${scale * 100}%`,
  };
}

function ResizableClip({
  scale,
  offsetX,
  offsetY,
  onScale,
  onMove,
}: {
  scale: number;
  offsetX: number;
  offsetY: number;
  onScale: (scale: number, commit: boolean) => void;
  onMove: (offsetX: number, offsetY: number, commit: boolean) => void;
}) {
  const scaled = scale < 1;
  const drag = (event: React.PointerEvent, apply: (dx: number, dy: number, frame: DOMRect) => void, finish: () => void) => {
    event.preventDefault();
    event.stopPropagation();
    const frame = (event.currentTarget as HTMLElement).closest('[data-preview-frame]')?.getBoundingClientRect();
    if (!frame) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let moved = false;
    const move = (ev: PointerEvent) => {
      moved = true;
      apply(ev.clientX - startX, ev.clientY - startY, frame);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (moved) finish();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      className={`absolute z-[15] outline outline-2 outline-teal-300 ${scaled ? 'cursor-move' : 'pointer-events-none'}`}
      style={scaled ? scaledBox(scale, offsetX, offsetY) : { inset: 0, outlineOffset: -2 }}
      onPointerDown={(event) => {
        if (!scaled) return;
        let latest = { x: offsetX, y: offsetY };
        drag(
          event,
          (dx, dy, frame) => {
            const limit = (1 - scale) / 2;
            latest = {
              x: Math.max(-limit, Math.min(limit, offsetX + dx / frame.width)),
              y: Math.max(-limit, Math.min(limit, offsetY - dy / frame.height)),
            };
            onMove(latest.x, latest.y, false);
          },
          () => onMove(latest.x, latest.y, true),
        );
      }}
    >
      <button
        type="button"
        aria-label="Resize clip"
        title="Drag to resize"
        className="absolute right-1 bottom-1 z-30 h-4 w-4 rounded-full border-2 border-teal-300 bg-white shadow cursor-nwse-resize touch-none pointer-events-auto"
        onPointerDown={(event) => {
          const box = event.currentTarget.parentElement?.getBoundingClientRect();
          if (!box) return;
          const cx = box.left + box.width / 2;
          const cy = box.top + box.height / 2;
          const reach = (x: number, y: number) =>
            Math.max(Math.abs(x - cx) / box.width, Math.abs(y - cy) / box.height);
          const sx = event.clientX;
          const sy = event.clientY;
          const startReach = Math.max(0.05, reach(sx, sy));
          let latest = scale;
          drag(
            event,
            (dx, dy) => {
              latest = Math.max(MIN_CLIP_SCALE, Math.min(1, scale * (reach(sx + dx, sy + dy) / startReach)));
              onScale(latest, false);
            },
            () => onScale(latest, true),
          );
        }}
      />
    </div>
  );
}

function ResizableLogo({
  src,
  scale,
  min,
  max,
  selected,
  showHandle,
  motionStyle,
  label,
  placement,
  onSelect,
  onScale,
}: {
  src: string;
  scale: number;
  min: number;
  max: number;
  selected: boolean;
  showHandle: boolean;
  motionStyle?: React.CSSProperties;
  label: string;
  placement?: { x: number; y: number };
  onSelect: () => void;
  onScale: (scale: number, commit: boolean) => void;
}) {
  const x = placement?.x ?? 0.5;
  const y = placement?.y ?? 0.5;
  return (
    <div
      className={`absolute z-20 ${selected ? 'outline outline-2 outline-teal-300 outline-offset-2' : ''}`}
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        width: `${scale * 100}%`,
        maxHeight: '92%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        className="block w-full h-auto max-h-[92cqh] object-contain pointer-events-none"
        style={motionStyle}
      />
      <button
        type="button"
        aria-label={label}
        title="Click, then drag the corner to resize"
        className="absolute inset-0 cursor-pointer"
        onPointerDown={(event) => {
          event.stopPropagation();
          onSelect();
        }}
      />
      {showHandle && (
        <button
          type="button"
          aria-label={`Resize ${label.toLowerCase()}`}
          title="Drag to resize"
          className="absolute right-1 bottom-1 z-30 h-4 w-4 rounded-full border-2 border-teal-300 bg-white shadow cursor-nwse-resize touch-none"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect();
            const wrap = event.currentTarget.parentElement;
            if (!wrap) return;
            const rect = wrap.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const startDx = Math.max(16, Math.abs(event.clientX - centerX));
            const startScale = scale;
            let latest = scale;
            let moved = false;
            const move = (ev: PointerEvent) => {
              const dx = Math.abs(ev.clientX - centerX);
              latest = Math.max(min, Math.min(max, startScale * (dx / startDx)));
              moved = true;
              onScale(latest, false);
            };
            const up = () => {
              window.removeEventListener('pointermove', move);
              window.removeEventListener('pointerup', up);
              if (moved) onScale(latest, true);
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
          }}
        />
      )}
    </div>
  );
}
